import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { NativeSelect } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RecurrenceFields, recDefaults, recPayload } from "@/components/RecurrenceFields";
import { useRecurringGuard } from "@/components/RecurringGuard";
import { WorkTypeChecks, applyWorkTypes } from "@/components/WorkTypeChecks";

const empty = () => ({ title: "", work_types: [], description: "", commessa_id: "", cimitero_id: "", area: "", due: "", work_date: "",
  priority: "normale", assignee_ids: [], delegate_ids: [], requires_verification: true, requires_photos: false, checklist: "", required_qualifications: "" });

export const AssigneePicker = ({ commessaId, value, onChange, testid = "assignee-picker" }) => {
  const [people, setPeople] = useState([]);
  useEffect(() => {
    if (commessaId) api.get("/users", { params: { commessa_id: commessaId } }).then((r) => setPeople(r.data.filter((u) => ["operatore", "caposquadra"].includes(u.role) && u.active)));
  }, [commessaId]);
  if (!commessaId) return <p className="text-xs text-muted-foreground">Seleziona prima la commessa</p>;
  return (
    <div className="max-h-44 space-y-1 overflow-y-auto rounded-xl border p-2" data-testid={testid}>
      {people.map((p) => (
        <label key={p.id} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-muted">
          <input type="checkbox" data-testid={`${testid}-${p.id}`} checked={value.includes(p.id)}
            onChange={(e) => onChange(e.target.checked ? [...value, p.id] : value.filter((x) => x !== p.id))} />
          <span className="font-medium">{p.name}</span><span className="text-xs text-muted-foreground">{p.role}</span>
        </label>
      ))}
      {people.length === 0 && <p className="p-2 text-xs text-muted-foreground">Nessun operatore assegnato alla commessa</p>}
    </div>
  );
};

export const ResponsabiliPicker = ({ commessaId, value, onChange, testid = "delegate-picker" }) => {
  const [people, setPeople] = useState([]);
  useEffect(() => {
    if (commessaId) api.get("/users", { params: { commessa_id: commessaId, role: "responsabile" } }).then((r) => setPeople(r.data.filter((u) => u.active)));
  }, [commessaId]);
  if (!commessaId) return <p className="text-xs text-muted-foreground">Seleziona prima la commessa</p>;
  return (
    <div className="max-h-44 space-y-1 overflow-y-auto rounded-xl border p-2" data-testid={testid}>
      {people.map((p) => (
        <label key={p.id} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-muted">
          <input type="checkbox" data-testid={`${testid}-${p.id}`} checked={value.includes(p.id)}
            onChange={(e) => onChange(e.target.checked ? [...value, p.id] : value.filter((x) => x !== p.id))} />
          <span className="font-medium">{p.name}</span>
        </label>
      ))}
      {people.length === 0 && <p className="p-2 text-xs text-muted-foreground">Nessun responsabile assegnato alla commessa</p>}
    </div>
  );
};

export const OrderFormDialog = ({ open, onOpenChange, commesse, onCreated, preset }) => {
  const { user } = useAuth();
  const [f, setF] = useState(empty());
  const [rec, setRec] = useState(null);
  const { guard, dialog } = useRecurringGuard();
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const cims = commesse.find((c) => c.id === f.commessa_id)?.cimiteri || [];

  useEffect(() => {
    if (open) { setF({ ...empty(), ...(preset || {}) }); setRec(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (suspend = false) => {
    setBusy(true);
    try {
      const body = { ...f, due: rec || !f.due ? "" : new Date(f.due).toISOString(), work_date: rec ? null : f.work_date || null, delegate_ids: rec ? [] : f.delegate_ids, cimitero_id: f.cimitero_id || null, area_id: f.area_id || null, criticita_id: f.criticita_id || null,
        checklist: f.checklist.split("\n").map((s) => s.trim()).filter(Boolean),
        required_qualifications: f.required_qualifications.split(",").map((s) => s.trim()).filter(Boolean) };
      if (rec) {
        await api.post("/recurrences", { ...body, ...recPayload(rec) });
        toast.success("Ordine ricorrente creato");
        onOpenChange(false);
        onCreated?.();
        nav("/ricorrenze");
        return;
      }
      const { data } = await api.post("/orders", { ...body, suspend_recurring: suspend });
      toast.success(`Ordine ${data.code} creato`);
      onOpenChange(false);
      onCreated?.();
      nav(`/ordini/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async () => {
    if (rec) return submit(false);
    try { await guard(f.assignee_ids, submit); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-2xl" data-testid="order-form-dialog">
        <DialogHeader><DialogTitle className="text-xl font-extrabold">Nuovo ordine di lavoro</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2"><Label>Titolo *</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="order-form-title" /></div>
          <WorkTypeChecks value={f.work_types} onChange={(v) => setF((x) => applyWorkTypes(x, v))} />
          <div className="space-y-1.5 sm:col-span-2"><Label>Descrizione</Label><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="order-form-description" /></div>
          <div className="space-y-1.5"><Label>Commessa *</Label>
            <NativeSelect className="w-full" testid="order-form-commessa" value={f.commessa_id} onChange={(v) => setF((x) => ({ ...x, commessa_id: v, cimitero_id: "", assignee_ids: [], delegate_ids: [] }))}>
              <option value="">Seleziona…</option>{commesse.map((c) => <option key={c.id} value={c.id}>{c.code} – {c.name}</option>)}
            </NativeSelect></div>
          <div className="space-y-1.5"><Label>Cimitero</Label>
            <NativeSelect className="w-full" testid="order-form-cimitero" value={f.cimitero_id} onChange={(v) => set("cimitero_id", v)}>
              <option value="">—</option>{cims.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </NativeSelect></div>
          {!rec && <div className="space-y-1.5"><Label>Data</Label><Input type="date" value={f.work_date} onChange={(e) => set("work_date", e.target.value)} data-testid="order-form-work-date" /></div>}
          {!rec && <div className="space-y-1.5"><Label>Scadenza</Label><Input type="datetime-local" value={f.due} onChange={(e) => set("due", e.target.value)} data-testid="order-form-due" /></div>}
          {!rec && <p className="-mt-2 text-xs text-muted-foreground sm:col-span-2" data-testid="order-form-date-hint">Indica almeno la Data o la Scadenza. Con la sola Data l'ordine non diventa mai scaduto.</p>}
          {!f.delegate_ids.length && <div className="space-y-1.5 sm:col-span-2"><Label>Assegna subito a (facoltativo)</Label><AssigneePicker commessaId={f.commessa_id} value={f.assignee_ids} onChange={(v) => set("assignee_ids", v)} testid="order-form-assignees" /></div>}
          {user.role === "ceo" && !rec && !f.assignee_ids.length && <div className="space-y-1.5 sm:col-span-2"><Label>Oppure affida a responsabili (facoltativo)</Label><ResponsabiliPicker commessaId={f.commessa_id} value={f.delegate_ids} onChange={(v) => set("delegate_ids", v)} testid="order-form-delegates" /><p className="text-xs text-muted-foreground">I responsabili scelti ricevono l'ordine e lo assegnano a un operatore.</p></div>}
          <div className="space-y-1.5 sm:col-span-2"><Label>Checklist (una voce per riga)</Label><Textarea value={f.checklist} onChange={(e) => set("checklist", e.target.value)} data-testid="order-form-checklist" /></div>
          <label className="flex items-center justify-between rounded-xl border p-3 text-sm font-medium">Richiede verifica del responsabile<Switch checked={f.requires_verification} onCheckedChange={(v) => set("requires_verification", v)} data-testid="order-form-verification-switch" /></label>
          <label className="flex items-center justify-between rounded-xl border p-3 text-sm font-medium">Foto prima/dopo obbligatorie<Switch checked={f.requires_photos} onCheckedChange={(v) => set("requires_photos", v)} data-testid="order-form-photos-switch" /></label>
          {!preset && <label className="flex items-center justify-between rounded-xl border border-primary/40 p-3 text-sm font-bold sm:col-span-2">Ordine ricorrente<Switch checked={!!rec} onCheckedChange={(v) => setRec(v ? recDefaults() : null)} data-testid="order-form-recurring-switch" /></label>}
          {rec && <RecurrenceFields value={rec} onChange={setRec} />}
        </div>
        <DialogFooter><Button disabled={busy || !f.title || !f.commessa_id || (rec && !rec.weekdays.length) || (!rec && !f.due && !f.work_date)} onClick={onSubmit} className="h-11 rounded-xl font-bold" data-testid="order-form-submit">{rec ? "Crea ricorrenza" : "Crea ordine"}</Button></DialogFooter>
        {dialog}
      </DialogContent>
    </Dialog>
  );
};
